// Patient-facing layout — desktop adaptation of the VitaMed patient-portal
// design language. Rendered by Schedules.js instead of the clinic UI when
// the signed-in user has the `patient` role. Sits under the app's normal
// navbar and routes between sub-pages that mirror the mockup:
//   /patient                 → home (greeting, hero, today's reminders, quick actions)
//   /patient/appointments    → upcoming + recent visits
//   /patient/pre-visit       → pre-visit welcome block
//   /patient/meds            → my medicines (morning / evening)
//   /patient/vitals          → log my vitals
//   /patient/message         → send a message
//   /patient/labs            → lab results
//   /patient/chart           → my chart + conditions + add note
//   /patient/request         → request an appointment (doctor → time → confirm)
//   /patient/wizard          → VitaMed Helper (text-only, no recordings)
// Sub-pages share their data via `PatientDataProvider` so each navigation
// doesn't refetch.
//
// A patient CANNOT create or edit an appointment row directly. The Add / Edit
// modals used to be mounted here, but AddPatientModal inserts into whatever
// schema its client is bound to — for a patient that is the app default, not
// the doctor's tenant schema — so the appointment never reached the doctor's
// schedule, and nothing checked availability or approved it. Everything now
// goes through /patient/request and the clinic's Appointments inbox.
import React from "react";
import {
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  PatientDataProvider,
  firstNameOf,
  greetingForHour,
  usePatientData,
} from "./patient/PatientDataContext";
import PatientHome from "./patient/PatientHome";
import PatientAppointments from "./patient/PatientAppointments";
import PatientPreVisit from "./patient/PatientPreVisit";
import PatientMeds from "./patient/PatientMeds";
import PatientVitals from "./patient/PatientVitals";
import PatientMessage from "./patient/PatientMessage";
import PatientLabs from "./patient/PatientLabs";
import PatientChart from "./patient/PatientChart";
import PatientRequestAppointment from "./patient/PatientRequestAppointment";
import PatientWizard from "./patient/PatientWizard";
import "./css/PatientPortal.css";

const NAV_ITEMS = [
  { to: ".", label: "Home", icon: "⌂", end: true },
  { to: "appointments", label: "Visits", icon: "📅" },
  { to: "meds", label: "Meds", icon: "℞" },
  { to: "message", label: "Messages", icon: "✉" },
  { to: "chart", label: "More", icon: "☰" },
];

function PortalHeader({ userRow, onRequest }) {
  const isVerified = userRow?.patient_match_status === "verified";
  return (
    <header className="pp-hdr">
      <div>
        <h1>
          {greetingForHour(new Date().getHours())}, {firstNameOf(userRow)}
        </h1>
        <div className="pp-greet-sub">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </div>
      </div>
      <div className="pp-hdr-actions">
        {isVerified && (
          <span className="pp-verified-badge">✓ Verified patient</span>
        )}
        <button
          type="button"
          className="pp-btn pp-coral pp-btn-compact"
          onClick={onRequest}
        >
          Request appointment
        </button>
      </div>
    </header>
  );
}

function PortalNav() {
  return (
    <nav className="pp-nav" aria-label="Patient portal sections">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `pp-nav-item${isActive ? " is-active" : ""}`
          }
        >
          <span className="pp-nav-icon" aria-hidden="true">{item.icon}</span>
          <span className="pp-nav-label">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function PortalChrome() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userRow } = usePatientData();
  // Rows are intentionally NOT passed via outlet context — sub-pages read
  // them from the data provider so they can re-render on updates without
  // depending on outlet memoization.

  return (
    <div className="pp-portal">
      <div className="pp-shell">
        <PortalHeader
          userRow={userRow}
          onRequest={() => navigate("request")}
        />

        <PortalNav />

        {/* Each sub-page renders into here. They share userRow / rows via
            PatientDataProvider, which lives one level up in PatientPortal. */}
        <div className="pp-page" key={location.pathname}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default function PatientPortal() {
  // Schedules.js mounts us at /schedules. The sub-routes match relative to
  // that URL, so navigating between Home / Visits / Meds etc. stays under
  // the same parent route (e.g. /schedules/patient/meds). The catch-all
  // route also redirects the bare /schedules URL into /schedules/patient so
  // the home page renders with consistent nav highlighting.
  return (
    <PatientDataProvider>
      <Routes>
        <Route path="patient" element={<PortalChrome />}>
          <Route index element={<PatientHome />} />
          <Route path="appointments" element={<PatientAppointments />} />
          <Route path="pre-visit" element={<PatientPreVisit />} />
          <Route path="meds" element={<PatientMeds />} />
          <Route path="vitals" element={<PatientVitals />} />
          <Route path="message" element={<PatientMessage />} />
          <Route path="labs" element={<PatientLabs />} />
          <Route path="chart" element={<PatientChart />} />
          <Route path="request" element={<PatientRequestAppointment />} />
          <Route path="wizard" element={<PatientWizard />} />
        </Route>
        <Route path="*" element={<NavigateToPatient />} />
      </Routes>
    </PatientDataProvider>
  );
}

function NavigateToPatient() {
  // Schedules.js mounts us at /schedules. Any path under it that isn't a real
  // /schedules/patient/* route lands here and gets pushed into the portal.
  //
  // A patient has no clinic day-grid, so the only way they reach a *dated*
  // /schedules?date=... URL is a clinic-style day/back navigation — e.g. the
  // visit page's IconRail "Schedule" button or the File menu. In that case they
  // were trying to move between their scheduled days, so send them to the Visits
  // list (where every day is pickable) instead of dumping them on Home. A bare
  // /schedules (fresh login) still lands on Home.
  const navigate = useNavigate();
  const location = useLocation();
  React.useEffect(() => {
    const hasDate = new URLSearchParams(location.search).has("date");
    navigate(hasDate ? "patient/appointments" : "patient", { replace: true });
  }, [navigate, location.pathname, location.search]);
  return null;
}